/**
 * `PaginatedGrid` — generic accumulator + grid for paginated `Page<T>` fetchers.
 *
 * Why it exists: every source list view (browse, search, favorites, history)
 * had its own "fetch once, render, never inspect hasMore" code path. This
 * component pairs with `usePaginatedFetcher` to load page 1 on mount, append
 * subsequent pages on demand via a "Load more" button (and, optionally, an
 * IntersectionObserver sentinel for auto-infinite-scroll), and surface error
 * banners without losing the already-loaded items.
 *
 * Usage:
 *
 *   <PaginatedGrid
 *     fetcher={(page) => source.getPopular({ page })}
 *     resetKey={source.id}
 *     renderItem={(comic) => <ComicCard comic={comic} />}
 *     errorBanner={(err) => <SourceErrorBanner error={err} sourceId={source.id} />}
 *   />
 *
 * The component is intentionally headless about the *kind* of item it
 * renders — pass any `renderItem(item, index)`. Callers can decide whether
 * to render blacklist-blocked items as placeholders (via `<BlockedComicCard>`)
 * or to drop them entirely.
 */
import { useEffect, useRef } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

import type { Page } from '@/sources/types';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { usePaginatedFetcher } from './usePaginatedFetcher';

export interface PaginatedGridProps<T> {
  /**
   * Async fetcher invoked with a 1-indexed page number. Errors propagate to
   * the inline error banner; prior pages stay rendered.
   */
  fetcher: (page: number, cursor?: string) => Promise<Page<T>>;
  /**
   * When this value changes the grid is reset and refetched. Use the
   * source id, search hash, or category id — whatever identifies the
   * "stream" the user is currently looking at.
   */
  resetKey?: unknown;
  /**
   * Preserves already-loaded pages for quick route returns (for example:
   * search results → detail → back) without re-issuing the same search.
   */
  cacheKey?: string;
  /** Per-item renderer. Receives the item and its flat (cross-page) index. */
  renderItem: (item: T, index: number) => React.ReactNode;
  /**
   * Stable key for each item. If omitted we use the flat index, which is
   * fine for static lists but causes React key warnings if items reorder.
   */
  itemKey?: (item: T, index: number) => string;
  /** Optional error renderer; falls back to a minimal inline alert if omitted. */
  errorBanner?: (error: unknown, retry: () => void) => React.ReactNode;
  /** Optional empty-state renderer when the first page returns zero items. */
  emptyState?: React.ReactNode;
  /**
   * If true, an IntersectionObserver-driven sentinel below the grid will
   * auto-fetch the next page when scrolled into view. Defaults to false
   * (deliberate user gesture only) — flip it on for mobile-first views.
   */
  autoLoadOnScroll?: boolean;
  /** Number of skeleton placeholders shown during the initial load. */
  skeletonCount?: number;
  /** Tailwind grid classes. Defaults to the existing 2/3/4/5 col layout. */
  gridClassName?: string;
  /** Wrapper className. */
  className?: string;
  /** ARIA label for the grid. Helpful for screen readers. */
  ariaLabel?: string;
}

const DEFAULT_GRID_CLASS =
  'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';

export function PaginatedGrid<T>({
  fetcher,
  resetKey,
  cacheKey,
  renderItem,
  itemKey,
  errorBanner,
  emptyState,
  autoLoadOnScroll = false,
  skeletonCount = 10,
  gridClassName,
  className,
  ariaLabel,
}: PaginatedGridProps<T>) {
  const { items, loading, error, hasMore, loadMore, retry, loadedPages } =
    usePaginatedFetcher<T>({ fetcher, resetKey, cacheKey });

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // IntersectionObserver auto-load (opt-in). The sentinel sits just below
  // the grid; once visible we trigger another page. We re-create the
  // observer when the trigger conditions change so we never fire while
  // already loading.
  useEffect(() => {
    if (!autoLoadOnScroll) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const node = sentinelRef.current;
    if (!node) return;
    if (!hasMore) return;
    if (loading) return;
    if (error !== null) return;

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            loadMore();
            break;
          }
        }
      },
      // 200px lookahead so loading kicks off before the user actually
      // reaches the bottom — keeps the scroll feeling continuous.
      { rootMargin: '200px 0px' },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [autoLoadOnScroll, hasMore, loading, error, loadMore]);

  // First-load skeletons. Once we have any items, we show the grid and
  // append a small inline spinner at the bottom while subsequent fetches
  // are in flight.
  const isInitialLoading = loading && loadedPages === 0;
  const isEmpty = !loading && error === null && items.length === 0 && loadedPages > 0;

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {isInitialLoading && (
        <div className={cn(gridClassName ?? DEFAULT_GRID_CLASS)} aria-label={ariaLabel}>
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <Skeleton key={i} className="aspect-[3/4] w-full" />
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div className={cn(gridClassName ?? DEFAULT_GRID_CLASS)} aria-label={ariaLabel}>
          {items.map((it, i) => (
            <RenderSlot
              key={itemKey ? itemKey(it, i) : i}
              item={it}
              index={i}
              render={renderItem}
            />
          ))}
        </div>
      )}

      {isEmpty && (emptyState ?? <DefaultEmptyState />)}

      {/* The errorBanner renderer is responsible for its own `role="alert"`
          (matching what `<SourceErrorBanner>` and the default banner below
          already provide). Wrapping in another `role="alert"` here would
          break `getByRole('alert')` in e2e tests by producing duplicates. */}
      {error !== null &&
        (errorBanner ? errorBanner(error, retry) : <DefaultErrorBanner error={error} retry={retry} />)}

      {hasMore && !isInitialLoading && (
        <div className="flex flex-col items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadMore}
            disabled={loading}
            aria-label="Load more"
          >
            {loading ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Loading…
              </>
            ) : (
              'Load more'
            )}
          </Button>
          {autoLoadOnScroll && (
            <div
              ref={sentinelRef}
              aria-hidden="true"
              className="h-px w-full"
              data-testid="paginated-grid-sentinel"
            />
          )}
        </div>
      )}

      {/* Bottom-of-list inline spinner for non-initial loads where the
          button is suppressed (e.g. error → retry → loading). */}
      {loading && !isInitialLoading && (
        <div
          className="flex items-center justify-center py-2 text-xs text-muted-foreground"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          Loading more…
        </div>
      )}
    </div>
  );
}

/**
 * Trivial wrapper so renderItem stays a pure function call from the parent's
 * perspective — keeps React DevTools cleaner than inlining the call.
 */
function RenderSlot<T>({
  item,
  index,
  render,
}: {
  item: T;
  index: number;
  render: (item: T, index: number) => React.ReactNode;
}) {
  return <>{render(item, index)}</>;
}

function DefaultEmptyState() {
  return (
    <p className="text-sm text-muted-foreground">No results.</p>
  );
}

function DefaultErrorBanner({ error, retry }: { error: unknown; retry: () => void }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
    >
      <div className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-destructive" />
        <span>{message}</span>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={retry}>
        Retry
      </Button>
    </div>
  );
}
